/* =========================================================================
   MapleWeld — Knowledge Hub behaviour.

   Four jobs, all optional — every one fails silent, because the article must
   stay readable if any of this breaks:
     1. reveal-on-scroll for [data-mwk-rv]
     2. the table of contents: built from the headings the writer actually
        wrote, so nobody maintains it by hand in the editor
     3. hiding a contents list the writer already typed into the article, so
        the page does not show two
     4. the reading-progress bar

   The TOC is generated rather than configured on purpose. These guides get
   edited often; a hand-kept contents list goes stale the first time someone
   renames a section, and a stale TOC is worse than none.
   ========================================================================= */
(function () {
  'use strict';

  var reduce = false;
  try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  /* ---------- 1. reveal ---------- */
  function reveal() {
    var els = document.querySelectorAll('[data-mwk-rv]');
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      for (var i = 0; i < els.length; i++) els[i].classList.add('is-in');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    for (var j = 0; j < els.length; j++) io.observe(els[j]);
  }

  /* ---------- helpers ---------- */

  // How far down the viewport the sticky header reaches right now. Measured
  // rather than hard-coded: the header shrinks when stuck and changes height
  // again at every breakpoint, so a constant would be wrong most of the time
  // and would park headings underneath the nav bar.
  function headerOffset() {
    var h = document.querySelector('.mwh-header');
    var n = h ? Math.round(h.getBoundingClientRect().height) : 0;
    if (!n || n > 260) n = 120;
    return n + 18;
  }

  function slug(text, used) {
    var s = String(text || '')
      .toLowerCase()
      .replace(/[‘’“”]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
    if (!s) s = 'section';
    var base = s, n = 2;
    while (used[s]) { s = base + '-' + n; n++; }
    used[s] = true;
    return s;
  }

  /* ---------- 3. the writer's own contents list ----------
     Several guides open with a hand-typed "In this guide" box. Once the
     sidebar exists that box is a second copy of the same links, so it is
     hidden — by CLASS, never removed, so switching the setting off (or
     printing the page) brings the original markup straight back.

     Target the LIST, not a container. The articles are pasted in wrapped in a
     single <div class="kh-article"> holding the whole guide, so any "first few
     children" scan sees one element with every link in the article inside it.
     Hiding that would blank the page.

     A list qualifies only when ALL of these hold:
       - it is a ul/ol with three or more links
       - every link in it points at an anchor on this same page
       - it sits in the top third of the article
     Then the wrapper is climbed at most two levels, and only while the parent
     carries barely more text than the list itself (a label such as "In this
     guide" plus the list, and nothing else), so the styled box goes with it.  */
  function hideDuplicateToc(rte) {
    var lists = rte.querySelectorAll('ul, ol');
    var rteTop = rte.getBoundingClientRect().top + window.pageYOffset;
    var cutoff = rteTop + Math.max(400, rte.offsetHeight * 0.33);

    for (var i = 0; i < lists.length; i++) {
      var list = lists[i];
      var all = list.querySelectorAll('a');
      if (all.length < 3) continue;

      var anchorsOnly = true;
      for (var k = 0; k < all.length; k++) {
        var href = all[k].getAttribute('href') || '';
        if (href.charAt(0) !== '#') { anchorsOnly = false; break; }
      }
      if (!anchorsOnly) continue;

      if (list.getBoundingClientRect().top + window.pageYOffset > cutoff) continue;

      var target = list;
      var listLen = (list.textContent || '').trim().length;
      var parent = list.parentElement;
      for (var lvl = 0; lvl < 2 && parent && parent !== rte; lvl++) {
        if (parent.classList.contains('mwa__rte')) break;
        var parentLen = (parent.textContent || '').trim().length;
        if (parentLen > listLen + 80) break;      // parent holds real content too
        target = parent;
        parent = parent.parentElement;
      }

      target.classList.add('mwa-dupe-toc');

      // a bare heading immediately above it ('Contents', 'In this guide')
      var prev = target.previousElementSibling;
      if (prev && /^h[1-6]$/i.test(prev.tagName) &&
          /contents|in this guide|jump to|on this page/i.test(prev.textContent || '')) {
        prev.classList.add('mwa-dupe-toc');
      }
      return true;
    }
    return false;
  }

  /* ---------- 2. table of contents ---------- */
  function toc() {
    var rte = document.querySelector('.mwa__rte');
    var host = document.querySelector('[data-mwk-toc]');
    var jump = document.querySelector('[data-mwk-jump]');
    var layout = document.querySelector('.mwa__layout');
    if (!rte) return;

    var nodes = rte.querySelectorAll('h2, h3');
    var heads = [];
    var used = {};
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var txt = (el.textContent || '').trim();
      if (!txt) continue;
      if (el.id) { used[el.id] = true; } else { el.id = slug(txt, used); }
      heads.push({ el: el, text: txt, sub: el.tagName.toLowerCase() === 'h3' });
    }

    // Fewer than three headings is a short post, not a document: a contents
    // panel beside it looks like scaffolding. Drop it and give the text the
    // full column instead.
    if (heads.length < 3) {
      if (host) host.remove();
      if (jump) jump.remove();
      if (layout) layout.classList.add('mwa__layout--full');
      return;
    }

    if (host && host.getAttribute('data-mwk-dedupe') === '1') {
      try { hideDuplicateToc(rte); } catch (e) {}
    }

    function build(listEl) {
      var frag = document.createDocumentFragment();
      heads.forEach(function (h) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'mwa__toclink' + (h.sub ? ' mwa__toclink--sub' : '');
        a.href = '#' + h.el.id;
        a.textContent = h.text;
        a.setAttribute('data-mwk-tl', h.el.id);
        li.appendChild(a);
        frag.appendChild(li);
      });
      listEl.appendChild(frag);
    }

    var lists = [];
    if (host) { var l1 = host.querySelector('.mwa__toclist'); if (l1) { build(l1); lists.push(l1); } }
    if (jump) { var l2 = jump.querySelector('.mwa__toclist'); if (l2) { build(l2); lists.push(l2); } }
    if (!lists.length) return;

    // smooth scroll with the measured header offset, and keep the URL shareable
    document.addEventListener('click', function (ev) {
      var a = ev.target && ev.target.closest ? ev.target.closest('[data-mwk-tl]') : null;
      if (!a) return;
      var target = document.getElementById(a.getAttribute('data-mwk-tl'));
      if (!target) return;
      ev.preventDefault();
      var y = target.getBoundingClientRect().top + window.pageYOffset - headerOffset();
      window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' });
      if (history.replaceState) history.replaceState(null, '', '#' + target.id);
      if (jump) jump.open = false;
    });

    /* scrollspy — the current section is the last heading above the fold line */
    var links = document.querySelectorAll('[data-mwk-tl]');
    var current = null;
    function spy() {
      var line = headerOffset() + 40;
      var active = heads[0].el.id;
      for (var i = 0; i < heads.length; i++) {
        if (heads[i].el.getBoundingClientRect().top <= line) active = heads[i].el.id;
        else break;
      }
      // at the very bottom of the page the last section is the one being read,
      // even if its heading never crosses the line on a short final section
      if (window.innerHeight + window.pageYOffset >= document.documentElement.scrollHeight - 80) {
        active = heads[heads.length - 1].el.id;
      }
      if (active === current) return;
      current = active;
      for (var j = 0; j < links.length; j++) {
        var on = links[j].getAttribute('data-mwk-tl') === active;
        links[j].classList.toggle('is-current', on);
        if (on && host) {
          var box = host.querySelector('.mwa__tocin');
          if (box && box.scrollHeight > box.clientHeight) {
            var lt = links[j].offsetTop, lh = links[j].offsetHeight;
            if (lt < box.scrollTop || lt + lh > box.scrollTop + box.clientHeight) {
              box.scrollTop = lt - box.clientHeight / 2 + lh / 2;
            }
          }
        }
      }
    }
    return spy;
  }

  /* ---------- 4. reading progress ---------- */
  function progress() {
    var bar = document.querySelector('[data-mwk-bar]');
    var rte = document.querySelector('.mwa__rte');
    if (!bar || !rte) return;
    return function () {
      var start = rte.getBoundingClientRect().top + window.pageYOffset;
      var span = rte.offsetHeight - window.innerHeight * 0.4;
      if (span < 1) { bar.style.width = '0%'; return; }
      var p = (window.pageYOffset - start + window.innerHeight * 0.25) / span;
      bar.style.width = Math.max(0, Math.min(1, p)) * 100 + '%';
    };
  }

  function start() {
    try { reveal(); } catch (e) {}

    var jobs = [];
    try { var a = toc(); if (a) jobs.push(a); } catch (e) {}
    try { var b = progress(); if (b) jobs.push(b); } catch (e) {}
    if (!jobs.length) return;

    var ticking = false;
    function run() {
      ticking = false;
      for (var i = 0; i < jobs.length; i++) { try { jobs[i](); } catch (e) {} }
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(run);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    run();

    // a direct link to #some-heading lands under the sticky header otherwise
    if (location.hash) {
      var t = document.getElementById(location.hash.slice(1));
      if (t) setTimeout(function () {
        window.scrollTo({ top: t.getBoundingClientRect().top + window.pageYOffset - headerOffset(), behavior: 'auto' });
      }, 60);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}());
